import { useEffect, useState } from 'react'
import { ErrorDeApi } from '@nucleo/api/cliente'
import { obtenerVistaPreviaOficial } from './clienteDeMatrices'
import type { FilaDeVistaPrevia, VistaPreviaOficial } from './tipos'

export function VistaPreviaMiper({ matrizId, revision, alCargar }: { matrizId: string; revision: number; alCargar: (revision: number | null) => void }) {
  const [vista, setVista] = useState<VistaPreviaOficial | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let vigente = true
    alCargar(null)
    setVista(null)
    setError(null)
    obtenerVistaPreviaOficial(matrizId)
      .then((resultado) => {
        if (!vigente) return
        if (resultado.revision !== revision) {
          setError('La revisión de la vista previa no coincide con la matriz. Actualiza la página antes de descargar.')
          return
        }
        setVista(resultado)
        alCargar(resultado.revision)
      })
      .catch((e: unknown) => {
        if (vigente) setError(e instanceof ErrorDeApi ? e.message : 'No fue posible cargar la vista previa oficial.')
      })
    return () => { vigente = false }
  }, [matrizId, revision, alCargar])

  return (
    <section className="sst-subpaso" aria-labelledby="titulo-vista-previa" data-paso="4.2">
      <div className="sst-subpaso-titulo">
        <span className="sst-subpaso-numero" aria-hidden="true">4.2</span>
        <div>
          <h3 id="titulo-vista-previa"><span className="sr-only">Paso 4.2: </span>Revisa la MIPER oficial antes de descargarla</h3>
          <p>Es la hoja MIPER del Excel, tal como se exporta.</p>
        </div>
      </div>
      {error ? <p role="alert" className="rounded-md bg-critico-fondo p-3 text-critico-texto">{error}</p> : null}
      {!vista && !error ? <p role="status" className="text-pequeno text-texto-secundario">Cargando la revisión oficial…</p> : null}
      {vista ? (
        <>
          <p className="text-pequeno text-texto-secundario">
            Revisión {vista.revision} · {vista.filas.length} fila(s) · confirmada por {vista.confirmada_por} el {new Date(vista.confirmada_en).toLocaleString('es-CL')}.
            {` `}Se muestra el snapshot oficial que genera la hoja MIPER del Excel. Si abriste un borrador posterior, sus cambios no aparecen aquí.
          </p>
          <dl className="grid gap-2 rounded-md bg-app p-3 text-pequeno sm:grid-cols-2">
            <div><dt className="font-semibold">Entidad / centro</dt><dd>{vista.alcance.empresa.razon_social} · {vista.alcance.centro.nombre}</dd></div>
            <div><dt className="font-semibold">Alcance / responsable</dt><dd>{vista.alcance.alcance} · {vista.alcance.responsable.nombre}</dd></div>
          </dl>
          <details className="rounded-md border border-borde p-3 text-pequeno">
            <summary className="cursor-pointer font-semibold">Qué representa cada columna del Excel</summary>
            <dl className="mt-3 grid gap-2 sm:grid-cols-2">
              {vista.encabezados.map((encabezado, indice) => (
                <div key={encabezado}><dt className="font-semibold">{columnaExcel(indice)} · {encabezado}</dt><dd className="text-texto-secundario">{vista.guia_columnas[indice]}</dd></div>
              ))}
            </dl>
          </details>
          {/* Permite desplazar las 17 columnas también con teclado. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <div className="sst-tabla-marco" role="region" aria-label="Hoja MIPER, desplazar horizontalmente para ver las 17 columnas" tabIndex={0}>
            <table className="sst-tabla min-w-[1900px]">
              <caption className="sr-only">Hoja MIPER oficial, columnas B a R y filas desde la 13</caption>
              <thead>
                <tr>
                  <th scope="col" className="sst-col-fija w-28">Fila Excel</th>
                  {vista.encabezados.map((encabezado, indice) => <th key={encabezado} scope="col" className="min-w-28 whitespace-normal"><span className="text-marca">{columnaExcel(indice)}</span><br />{encabezado}</th>)}
                </tr>
              </thead>
              <tbody>
                {vista.filas.map((fila) => <FilaDeMiper key={fila.fila_id} fila={fila} />)}
              </tbody>
            </table>
          </div>
          <p className="text-pequeno text-texto-secundario">Las celdas «—» quedan vacías en el Excel porque esa clase de riesgo se evalúa en otro grupo de columnas. Abre «Por qué se ubicó así» bajo cada fila para revisar su fundamento y validación.</p>
        </>
      ) : null}
    </section>
  )
}

function FilaDeMiper({ fila }: { fila: FilaDeVistaPrevia }) {
  const detalle = fila.detalle
  const evaluacion = detalle.evaluacion
  return (
    <>
      <tr>
        <th scope="row" className="sst-col-fija font-semibold">{fila.numero_excel}</th>
        {fila.valores.map((valor, indice) => <td key={indice} className="min-w-28 whitespace-pre-line">{valor === null || valor === '' ? <span className="text-texto-secundario">—</span> : valor}</td>)}
      </tr>
      <tr>
        <td colSpan={18}>
          <details>
            <summary className="cursor-pointer font-semibold">Fila {fila.numero_excel}: por qué se ubicó así</summary>
            <div className="mt-3 grid gap-2 text-pequeno md:grid-cols-2">
              <Explicacion titulo="Ubicación en columnas">{detalle.ubicacion}</Explicacion>
              <Explicacion titulo="Peligro y riesgo">{detalle.clasificacion} → {detalle.familia} → {detalle.riesgo}. {detalle.justificacion || 'No se registró justificación adicional.'}</Explicacion>
              <Explicacion titulo="Evaluación">
                {evaluacion.tipo === 'motor'
                  ? `P ${evaluacion.probabilidad} × C ${evaluacion.consecuencia} = VEP ${evaluacion.vep}; nivel ${evaluacion.nivel_etiqueta}. ${evaluacion.accion}`
                  : `${evaluacion.protocolo_sugerido ?? 'Protocolo aplicable'}; ${evaluacion.consolidada ? `resultado de ${detalle.fuente_protocolo ?? 'fuente técnica no indicada'}` : 'resultado aún pendiente de protocolo'}.`}
              </Explicacion>
              <Explicacion titulo="Base y visto bueno">{detalle.base_de_propuesta}. Validada por {detalle.validada_por ?? 'sin identificación'}{detalle.validada_en ? ` el ${new Date(detalle.validada_en).toLocaleString('es-CL')}` : ''}.</Explicacion>
              <Explicacion titulo="Medidas y prelación">{detalle.medidas.map((medida) => `${medida.prelacion_etiqueta}: ${medida.descripcion}`).join(' · ') || 'Sin medidas registradas.'}</Explicacion>
              <Explicacion titulo="Versiones">Motor {detalle.version_motor} · catálogo {detalle.version_catalogo}.</Explicacion>
            </div>
          </details>
        </td>
      </tr>
    </>
  )
}

function Explicacion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return <div><strong>{titulo}</strong><p className="m-0 text-texto-secundario">{children}</p></div>
}

function columnaExcel(indice: number): string { return String.fromCharCode(66 + indice) }
